import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Home, Clock, FileText, DollarSign, User, LogOut, Crown, Link2, Bell, Shield, MoreHorizontal, Settings, ChevronRight, X, CalendarDays, Sun, Moon, Monitor, Menu, ChevronLeft, HelpCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { APP_VERSION } from '../version';
import { useTheme } from '../contexts/ThemeContext';
import NotificationBell from './NotificationBell';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme: themePref, setTheme } = useTheme();
  const [currentPlan, setCurrentPlan] = useState('free');
  const [mobileProfileOpen, setMobileProfileOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [navSectionOpen, setNavSectionOpen] = useState(true);
  const [settingsSectionOpen, setSettingsSectionOpen] = useState(true);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;
  
  const isParent = user?.role === 'parent';
  const isNanny = user?.role === 'nanny';
  
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const response = await axios.get(`${API}/payments/limits/check`);
        setCurrentPlan(response.data.current_plan || 'free');
      } catch (error) {
        console.error('Error fetching plan:', error);
      }
    };
    
    if (user) {
      fetchPlan();
    }
    
    // Écouter les événements de changement de plan
    const handlePlanUpdate = () => {
      if (user) {
        fetchPlan();
      }
    };
    
    window.addEventListener('planUpdated', handlePlanUpdate);
    
    return () => {
      window.removeEventListener('planUpdated', handlePlanUpdate);
    };
  }, [user?.id]);
  
  const navItems = [
    { path: '/accueil', label: 'Accueil', icon: Home },
    { path: '/heures', label: 'Heures', icon: Clock },
    { path: '/contrats', label: 'Contrats', icon: FileText },
    { path: '/paie', label: 'Paie', icon: DollarSign },
  ];
  
  // Mobile nav items: 4 pages + Profile button (handled separately)
  const mobileNavItems = isNanny
    ? [
        { path: '/accueil', label: 'Accueil', icon: Home },
        { path: '/heures', label: 'Heures', icon: Clock },
        { path: '/contrats', label: 'Contrats', icon: FileText },
      ]
    : navItems;
  
  const getRoleBadge = (role) => {
    if (role === 'nanny') {
      return <Badge variant="outline" className="bg-[var(--primary-100)] text-[var(--primary-400)] border-[var(--primary-400)]">Nounou</Badge>;
    }
    return <Badge variant="outline" className="bg-[var(--secondary-50)] text-[var(--secondary-400)] border-[var(--secondary-400)]">Parent</Badge>;
  };

  const handleMobileNavItem = (path) => {
    setMobileProfileOpen(false);
    navigate(path);
  };

  const handleSidebarNavItem = (path) => {
    navigate(path);
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  // Update body class for sidebar state
  useEffect(() => {
    // Add has-sidebar class when Navigation mounts (authenticated pages)
    document.body.classList.add('has-sidebar');
    
    if (sidebarCollapsed) {
      document.body.classList.add('sidebar-collapsed');
    } else {
      document.body.classList.remove('sidebar-collapsed');
    }
    
    // Cleanup when leaving authenticated pages
    return () => {
      document.body.classList.remove('has-sidebar');
      document.body.classList.remove('sidebar-collapsed');
    };
  }, [sidebarCollapsed]);
  
  return (
    <>
      {/* Mobile Bottom Navigation */}
      <nav 
        className="fixed bottom-0 left-0 right-0 bg-[var(--bg-primary)] border-t border-[var(--neutral-200)] z-50 md:hidden"
        data-testid="bottom-navigation"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex justify-around items-center h-16">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={cn(
                  "flex flex-col items-center justify-center flex-1 h-full transition-colors duration-200",
                  isActive 
                    ? "text-[var(--primary-400)]" 
                    : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                )}
                data-testid={`nav-${item.label.toLowerCase()}`}
              >
                <Icon className="h-5 w-5 mb-1" />
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            );
          })}
          
          {/* Mobile Profile Button - Opens Sheet */}
          <Sheet open={mobileProfileOpen} onOpenChange={setMobileProfileOpen}>
            <SheetTrigger asChild>
              <button
                className={cn(
                  "flex flex-col items-center justify-center flex-1 h-full transition-colors duration-200",
                  mobileProfileOpen
                    ? "text-[var(--primary-400)]"
                    : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
                )}
                data-testid="nav-mobile-profile-trigger"
              >
                <div className="relative">
                  <MoreHorizontal className="h-5 w-5 mb-1" />
                  {['premium', 'parent_pro', 'nanny_pro'].includes(currentPlan) && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-[var(--secondary-400)]" />
                  )}
                </div>
                <span className="text-xs font-medium">Plus</span>
              </button>
            </SheetTrigger>
            <SheetContent 
              side="bottom" 
              className="rounded-t-2xl bg-[var(--bg-primary)] px-0 pb-8"
              data-testid="mobile-profile-sheet"
            >
              <SheetHeader className="px-6 pb-4">
                <SheetTitle className="text-left text-lg" style={{ fontFamily: 'var(--font-secondary)', color: 'var(--text-primary)' }}>
                  Mon profil
                </SheetTitle>
              </SheetHeader>
              
              {/* User Info Card */}
              <div className="px-6 pb-4">
                <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--primary-50)' }}>
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'var(--primary-100)', color: 'var(--primary-400)' }}
                  >
                    <User className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                        {user?.full_name}
                      </p>
                      {['premium', 'parent_pro', 'nanny_pro'].includes(currentPlan) && (
                        <Badge className="bg-[var(--secondary-400)] text-white shrink-0" style={{ fontSize: '10px' }}>
                          <Crown className="h-3 w-3 mr-1" />
                          Pro
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                      {user?.email}
                    </p>
                    <div className="mt-1">
                      {getRoleBadge(user?.role)}
                    </div>
                  </div>
                </div>
              </div>
              
              <Separator />
              
              {/* Menu Items */}
              <div className="py-2">
                {/* Mon profil */}
                <button
                  onClick={() => handleMobileNavItem('/profil')}
                  className="flex items-center gap-3 w-full px-6 py-3 text-left transition-colors duration-200 hover:bg-[var(--neutral-100)]"
                  data-testid="mobile-profile-myprofile"
                >
                  <User className="h-5 w-5" style={{ color: 'var(--primary-400)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Mon profil</span>
                  <ChevronRight className="h-4 w-4 ml-auto" style={{ color: 'var(--text-secondary)' }} />
                </button>

                {/* Notifications */}
                <button
                  onClick={() => handleMobileNavItem('/notifications')}
                  className="flex items-center gap-3 w-full px-6 py-3 text-left transition-colors duration-200 hover:bg-[var(--neutral-100)]"
                  data-testid="mobile-profile-notifications"
                >
                  <Bell className="h-5 w-5" style={{ color: 'var(--primary-400)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Notifications</span>
                  <ChevronRight className="h-4 w-4 ml-auto" style={{ color: 'var(--text-secondary)' }} />
                </button>
                
                {/* Billing */}
                <button
                    onClick={() => handleMobileNavItem('/billing')}
                    className="flex items-center gap-3 w-full px-6 py-3 text-left transition-colors duration-200 hover:bg-[var(--neutral-100)]"
                    data-testid="mobile-profile-billing"
                  >
                    <Crown className="h-5 w-5" style={{ color: 'var(--secondary-400)' }} />
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Abonnement</span>
                    {currentPlan === 'free' && (
                      <Badge variant="outline" className="ml-auto text-[10px] border-[var(--secondary-400)] text-[var(--secondary-400)]">
                        Upgrade
                      </Badge>
                    )}
                    <ChevronRight className="h-4 w-4 ml-auto" style={{ color: 'var(--text-secondary)' }} />
                  </button>
                
                {/* Simulateur - Parent only - DÉSACTIVÉ */}
                {/* {isParent && (
                  <button
                    onClick={() => handleMobileNavItem('/simulateur')}
                    className="flex items-center gap-3 w-full px-6 py-3 text-left transition-colors duration-200 hover:bg-[var(--neutral-100)]"
                    data-testid="mobile-profile-simulateur"
                  >
                    <Settings className="h-5 w-5" style={{ color: 'var(--primary-400)' }} />
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Simulateur</span>
                    <ChevronRight className="h-4 w-4 ml-auto" style={{ color: 'var(--text-secondary)' }} />
                  </button>
                )} */}
                
                {/* Congés */}
                <button
                  onClick={() => handleMobileNavItem('/conges')}
                  className="flex items-center gap-3 w-full px-6 py-3 text-left transition-colors duration-200 hover:bg-[var(--neutral-100)]"
                  data-testid="mobile-profile-leaves"
                >
                  <CalendarDays className="h-5 w-5" style={{ color: 'var(--secondary-400)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Congés</span>
                  <ChevronRight className="h-4 w-4 ml-auto" style={{ color: 'var(--text-secondary)' }} />
                </button>
                
                {/* Besoin d'aide ? */}
                <button
                  onClick={() => handleMobileNavItem('/support')}
                  className="flex items-center gap-3 w-full px-6 py-3 text-left transition-colors duration-200 hover:bg-[var(--neutral-100)]"
                  data-testid="mobile-profile-support"
                >
                  <HelpCircle className="h-5 w-5" style={{ color: 'var(--secondary-400)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Besoin d'aide ?</span>
                  <ChevronRight className="h-4 w-4 ml-auto" style={{ color: 'var(--text-secondary)' }} />
                </button>
                
                {/* Administration - Admin & Support only */}
                {(user?.role === 'admin' || user?.role === 'support') && (
                  <button
                    onClick={() => handleMobileNavItem('/admin')}
                    className="flex items-center gap-3 w-full px-6 py-3 text-left transition-colors duration-200 hover:bg-[var(--neutral-100)]"
                    data-testid="mobile-profile-admin"
                  >
                    <Shield className="h-5 w-5" style={{ color: 'var(--error)' }} />
                    <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Administration</span>
                    <Badge variant="destructive" className="ml-auto text-[10px]">Admin</Badge>
                  </button>
                )}
                
                {/* RGPD & Legal */}
                <button
                  onClick={() => handleMobileNavItem('/legal')}
                  className="flex items-center gap-3 w-full px-6 py-3 text-left transition-colors duration-200 hover:bg-[var(--neutral-100)]"
                  data-testid="mobile-profile-legal"
                >
                  <Shield className="h-5 w-5" style={{ color: 'var(--primary-300)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>RGPD & Mentions</span>
                  <ChevronRight className="h-4 w-4 ml-auto" style={{ color: 'var(--text-secondary)' }} />
                </button>
              </div>
              
              <Separator />
              
              {/* Theme Toggle - Mobile */}
              <div className="px-6 py-3">
                <p className="text-xs font-medium mb-2" style={{ color: 'var(--text-muted)' }}>Thème</p>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => setTheme('light')}
                    className={cn("flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium transition-colors duration-150",
                      themePref === 'light' ? "bg-[var(--primary-100)] text-[var(--primary-600)]" : "text-[var(--text-secondary)] hover:bg-[var(--neutral-100)]")}
                    data-testid="mobile-theme-light-btn"
                  >
                    <Sun className="h-4 w-4" /> Clair
                  </button>
                  <button
                    onClick={() => setTheme('dark')}
                    className={cn("flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium transition-colors duration-150",
                      themePref === 'dark' ? "bg-[var(--primary-100)] text-[var(--primary-600)]" : "text-[var(--text-secondary)] hover:bg-[var(--neutral-100)]")}
                    data-testid="mobile-theme-dark-btn"
                  >
                    <Moon className="h-4 w-4" /> Sombre
                  </button>
                  <button
                    onClick={() => setTheme('system')}
                    className={cn("flex-1 flex items-center justify-center gap-1.5 px-2 py-2 rounded-lg text-xs font-medium transition-colors duration-150",
                      themePref === 'system' ? "bg-[var(--primary-100)] text-[var(--primary-600)]" : "text-[var(--text-secondary)] hover:bg-[var(--neutral-100)]")}
                    data-testid="mobile-theme-system-btn"
                  >
                    <Monitor className="h-4 w-4" /> Auto
                  </button>
                </div>
              </div>
              
              <Separator />
              
              {/* Logout + Version */}
              <div className="pt-2 px-6 flex items-center justify-between">
                <button
                  onClick={() => {
                    setMobileProfileOpen(false);
                    logout();
                  }}
                  className="flex items-center gap-3 py-3 text-left transition-colors duration-200 rounded-lg hover:bg-[var(--error-light)]"
                  data-testid="mobile-profile-logout"
                >
                  <LogOut className="h-5 w-5" style={{ color: 'var(--error)' }} />
                  <span className="text-sm font-medium" style={{ color: 'var(--error)' }}>Déconnexion</span>
                </button>
                <span
                  className="text-xs select-none"
                  style={{ color: 'var(--text-secondary)', opacity: 0.5 }}
                  data-testid="mobile-version"
                >
                  {APP_VERSION}
                </span>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
      
      {/* Desktop: Permanent Sidebar + Top Header */}
      <div className="hidden md:block">
        {/* Toggle Button - Trait qui devient flèche directement */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={toggleSidebar}
                className={cn(
                  "fixed top-1/2 -translate-y-1/2 z-[60] transition-all duration-200",
                  "flex items-center justify-center cursor-pointer group",
                  "text-[var(--primary-400)] hover:text-[var(--primary-500)]",
                  sidebarCollapsed 
                    ? "left-[79px]"
                    : "left-[287px]"
                )}
                data-testid="sidebar-toggle"
                aria-label={sidebarCollapsed ? "Développer le menu" : "Réduire le menu"}
              >
                {/* Trait par défaut */}
                <span className="absolute group-hover:opacity-0 group-hover:scale-0 transition-all duration-200 bg-[var(--primary-400)] w-0.5 h-7 rounded-full shadow-md" />
                
                {/* Flèche au hover */}
                <span className="opacity-0 scale-0 group-hover:opacity-100 group-hover:scale-100 transition-all duration-200">
                  {sidebarCollapsed ? (
                    <ChevronRight className="h-5 w-5 drop-shadow-md" />
                  ) : (
                    <ChevronLeft className="h-5 w-5 drop-shadow-md" />
                  )}
                </span>
              </button>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-[var(--neutral-800)] text-white border-none">
              <p>{sidebarCollapsed ? "Développer" : "Réduire"}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Permanent Sidebar */}
        <aside 
          className={cn(
            "fixed left-0 top-0 h-screen bg-[var(--bg-primary)] border-r border-[var(--neutral-200)] z-50 transition-all duration-300 overflow-y-auto flex flex-col",
            sidebarCollapsed ? "w-20" : "w-72"
          )}
          data-testid="desktop-sidebar"
        >

          {/* Sidebar Header - Logo Always Visible */}
          <div className="flex items-center justify-center h-20 px-4 border-b border-[var(--neutral-200)]">
            {sidebarCollapsed ? (
              <img 
                src="/assets/icon.png" 
                alt="Nannyou" 
                className="h-12 w-12 object-contain"
              />
            ) : (
              <div className="flex items-center gap-3 h-full w-full">
                <img 
                  src="/assets/logo.png" 
                  alt="Nannyou" 
                  className="h-full w-full object-contain"
                />
                {(() => {
                  const env = (process.env.REACT_APP_ENV || '').toLowerCase();
                  if (env === 'recette') {
                    return (
                      <span 
                        className="text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded"
                        style={{ 
                          color: '#FFFFFF',
                          backgroundColor: '#DC2626',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        R7
                      </span>
                    );
                  } else if (env === 'preprod') {
                    return (
                      <span 
                        className="text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded"
                        style={{ 
                          color: '#FFFFFF',
                          backgroundColor: '#F59E0B',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        PPR
                      </span>
                    );
                  } else if (['dev', 'development', 'staging'].includes(env)) {
                    return (
                      <span 
                        className="text-[10px] font-semibold tracking-wider px-1.5 py-0.5 rounded"
                        style={{ 
                          color: '#FFFFFF',
                          backgroundColor: '#DC2626',
                          fontFamily: 'var(--font-mono)',
                        }}
                      >
                        DEV
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </div>
          
          <Separator />
          
          {/* Navigation Items - Collapsible Section */}
          <div className="py-2 flex-1">
            {/* Navigation Section Header */}
            {!sidebarCollapsed && (
              <button
                onClick={() => setNavSectionOpen(!navSectionOpen)}
                className="flex items-center justify-between w-full px-4 py-2 hover:bg-[var(--neutral-100)] transition-colors"
              >
                <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>NAVIGATION</p>
                <ChevronRight 
                  className={cn("h-4 w-4 transition-transform", navSectionOpen && "rotate-90")} 
                  style={{ color: 'var(--text-muted)' }} 
                />
              </button>
            )}
            
            {/* Navigation Items */}
            {(navSectionOpen || sidebarCollapsed) && navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              
              return (
                <button
                  key={item.path}
                  onClick={() => handleSidebarNavItem(item.path)}
                  className={cn(
                    "flex items-center w-full transition-colors duration-200",
                    sidebarCollapsed ? "justify-center px-4 py-4" : "gap-3 px-4 py-3",
                    isActive 
                      ? "bg-[var(--primary-50)] text-[var(--primary-600)] border-l-4 border-[var(--primary-400)]" 
                      : "text-[var(--text-secondary)] hover:bg-[var(--neutral-100)] border-l-4 border-transparent"
                  )}
                  title={sidebarCollapsed ? item.label : undefined}
                  data-testid={`nav-desktop-${item.label.toLowerCase()}`}
                >
                  <Icon className="h-5 w-5 shrink-0" />
                  {!sidebarCollapsed && <span className="text-sm font-medium">{item.label}</span>}
                </button>
              );
            })}
            
            <Separator className="my-2" />
            
            {/* Settings Section Header */}
            {!sidebarCollapsed && (
              <button
                onClick={() => setSettingsSectionOpen(!settingsSectionOpen)}
                className="flex items-center justify-between w-full px-4 py-2 hover:bg-[var(--neutral-100)] transition-colors"
              >
                <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>PARAMÈTRES</p>
                <ChevronRight 
                  className={cn("h-4 w-4 transition-transform", settingsSectionOpen && "rotate-90")} 
                  style={{ color: 'var(--text-muted)' }} 
                />
              </button>
            )}
            
            {/* Settings Items */}
            {(settingsSectionOpen || sidebarCollapsed) && (
              <>
                {/* Mon profil */}
                <button
                  onClick={() => handleSidebarNavItem('/profil')}
                  className={cn(
                    "flex items-center w-full transition-colors duration-200 hover:bg-[var(--neutral-100)]",
                    sidebarCollapsed ? "justify-center px-4 py-4" : "gap-3 px-4 py-3"
                  )}
                  title={sidebarCollapsed ? "Mon profil" : undefined}
                  data-testid="desktop-sidebar-profile"
                >
                  <User className="h-5 w-5 shrink-0" style={{ color: 'var(--primary-400)' }} />
                  {!sidebarCollapsed && <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Mon profil</span>}
                </button>

                {/* Abonnement */}
                <button
                  onClick={() => handleSidebarNavItem('/billing')}
                  className={cn(
                    "flex items-center w-full transition-colors duration-200 hover:bg-[var(--neutral-100)]",
                    sidebarCollapsed ? "justify-center px-4 py-4" : "gap-3 px-4 py-3"
                  )}
                  title={sidebarCollapsed ? "Abonnement" : undefined}
                  data-testid="desktop-sidebar-billing"
                >
                  <Crown className="h-5 w-5 shrink-0" style={{ color: 'var(--secondary-400)' }} />
                  {!sidebarCollapsed && (
                    <>
                      <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Abonnement</span>
                      {currentPlan === 'free' && (
                        <Badge variant="outline" className="ml-auto text-[10px] border-[var(--secondary-400)] text-[var(--secondary-400)]">
                          Upgrade
                        </Badge>
                      )}
                    </>
                  )}
                </button>
                
                {/* Simulateur - Parent only - DÉSACTIVÉ */}
                {/* {isParent && (
                  <button
                    onClick={() => handleSidebarNavItem('/simulateur')}
                    className={cn(
                      "flex items-center w-full transition-colors duration-200 hover:bg-[var(--neutral-100)]",
                      sidebarCollapsed ? "justify-center px-4 py-4" : "gap-3 px-4 py-3"
                    )}
                    title={sidebarCollapsed ? "Simulateur" : undefined}
                    data-testid="desktop-sidebar-simulateur"
                  >
                    <Settings className="h-5 w-5 shrink-0" style={{ color: 'var(--primary-400)' }} />
                    {!sidebarCollapsed && <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Simulateur</span>}
                  </button>
                )} */}
                
                {/* Congés */}
                <button
                  onClick={() => handleSidebarNavItem('/conges')}
                  className={cn(
                    "flex items-center w-full transition-colors duration-200 hover:bg-[var(--neutral-100)]",
                    sidebarCollapsed ? "justify-center px-4 py-4" : "gap-3 px-4 py-3"
                  )}
                  title={sidebarCollapsed ? "Congés" : undefined}
                  data-testid="desktop-sidebar-leaves"
                >
                  <CalendarDays className="h-5 w-5 shrink-0" style={{ color: 'var(--secondary-400)' }} />
                  {!sidebarCollapsed && <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Congés</span>}
                </button>
                
                {/* Besoin d'aide ? - For all users */}
                <button
                  onClick={() => handleSidebarNavItem('/support')}
                  className={cn(
                    "flex items-center w-full transition-colors duration-200 hover:bg-[var(--neutral-100)]",
                    sidebarCollapsed ? "justify-center px-4 py-4" : "gap-3 px-4 py-3"
                  )}
                  title={sidebarCollapsed ? "Besoin d'aide ?" : undefined}
                  data-testid="desktop-sidebar-support"
                >
                  <HelpCircle className="h-5 w-5 shrink-0" style={{ color: 'var(--secondary-400)' }} />
                  {!sidebarCollapsed && <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Besoin d'aide ?</span>}
                </button>
                
                {/* Administration - Admin & Support only */}
                {(user?.role === 'admin' || user?.role === 'support') && (
                  <button
                    onClick={() => handleSidebarNavItem('/admin')}
                    className={cn(
                      "flex items-center w-full transition-colors duration-200 hover:bg-[var(--neutral-100)]",
                      sidebarCollapsed ? "justify-center px-4 py-4" : "gap-3 px-4 py-3"
                    )}
                    title={sidebarCollapsed ? "Administration" : undefined}
                    data-testid="desktop-sidebar-admin"
                  >
                    <Shield className="h-5 w-5 shrink-0" style={{ color: 'var(--error)' }} />
                    {!sidebarCollapsed && (
                      <>
                        <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Administration</span>
                        <Badge variant="destructive" className="ml-auto text-[10px]">
                          Admin
                        </Badge>
                      </>
                    )}
                  </button>
                )}
                
                {/* RGPD & Legal */}
                <button
                  onClick={() => handleSidebarNavItem('/legal')}
                  className={cn(
                    "flex items-center w-full transition-colors duration-200 hover:bg-[var(--neutral-100)]",
                    sidebarCollapsed ? "justify-center px-4 py-4" : "gap-3 px-4 py-3"
                  )}
                  title={sidebarCollapsed ? "RGPD & Mentions" : undefined}
                  data-testid="desktop-sidebar-legal"
                >
                  <Shield className="h-5 w-5 shrink-0" style={{ color: 'var(--primary-300)' }} />
                  {!sidebarCollapsed && <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>RGPD & Mentions</span>}
                </button>
              </>
            )}
          </div>
          
          <Separator />
          
          {/* Bottom Section: User Info + Logout */}
          <div className={cn("py-4", sidebarCollapsed ? "px-2" : "px-4")}>
            {/* User Info Card */}
            {!sidebarCollapsed && (
              <div className="mb-3">
                <div className="flex items-center gap-3 p-3 rounded-xl" style={{ backgroundColor: 'var(--primary-50)' }}>
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                    style={{ backgroundColor: 'var(--primary-100)', color: 'var(--primary-400)' }}
                  >
                    <User className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                        {user?.full_name}
                      </p>
                      {['premium', 'parent_pro', 'nanny_pro'].includes(currentPlan) && (
                        <Badge className="bg-[var(--secondary-400)] text-white shrink-0" style={{ fontSize: '10px' }}>
                          <Crown className="h-3 w-3 mr-1" />
                          Pro
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                      {user?.email}
                    </p>
                    <div className="mt-1">
                      {getRoleBadge(user?.role)}
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Logout */}
            <button
              onClick={logout}
              className={cn(
                "flex items-center w-full py-3 transition-colors duration-200 rounded-lg hover:bg-[var(--error-light)]",
                sidebarCollapsed ? "justify-center" : "gap-3 px-3"
              )}
              title={sidebarCollapsed ? "Déconnexion" : undefined}
              data-testid="desktop-sidebar-logout"
            >
              <LogOut className="h-5 w-5 shrink-0" style={{ color: 'var(--error)' }} />
              {!sidebarCollapsed && <span className="text-sm font-medium" style={{ color: 'var(--error)' }}>Déconnexion</span>}
            </button>
            
            {!sidebarCollapsed && (
              <>
                <Separator className="my-3" />
                <span
                  className="block text-center text-xs select-none"
                  style={{ color: 'var(--text-secondary)', opacity: 0.5 }}
                  data-testid="desktop-version"
                >
                  {APP_VERSION}
                </span>
              </>
            )}
          </div>
        </aside>

        {/* Top Header (Right side only) */}
        <header 
          className={cn(
            "fixed top-0 right-0 bg-[var(--bg-primary)] border-b border-[var(--neutral-200)] z-40 transition-all duration-300",
            sidebarCollapsed ? "left-20" : "left-72"
          )}
          data-testid="top-navigation"
        >
          <div className="flex items-center justify-end h-16 px-6 gap-4">
            {/* Notifications */}
            <NotificationBell />
            
            {/* Theme Toggle */}
            <div className="flex items-center gap-1.5 p-1 rounded-lg" style={{ backgroundColor: 'var(--neutral-100)' }}>
              <button
                onClick={() => setTheme('light')}
                className={cn(
                  "p-2 rounded-md transition-colors duration-150",
                  themePref === 'light' ? "bg-[var(--bg-primary)] shadow-sm" : "hover:bg-[var(--neutral-200)]"
                )}
                title="Thème clair"
                data-testid="theme-light-btn"
              >
                <Sun className="h-4 w-4" style={{ color: themePref === 'light' ? 'var(--primary-400)' : 'var(--text-secondary)' }} />
              </button>
              <button
                onClick={() => setTheme('dark')}
                className={cn(
                  "p-2 rounded-md transition-colors duration-150",
                  themePref === 'dark' ? "bg-[var(--bg-primary)] shadow-sm" : "hover:bg-[var(--neutral-200)]"
                )}
                title="Thème sombre"
                data-testid="theme-dark-btn"
              >
                <Moon className="h-4 w-4" style={{ color: themePref === 'dark' ? 'var(--primary-400)' : 'var(--text-secondary)' }} />
              </button>
              <button
                onClick={() => setTheme('system')}
                className={cn(
                  "p-2 rounded-md transition-colors duration-150",
                  themePref === 'system' ? "bg-[var(--bg-primary)] shadow-sm" : "hover:bg-[var(--neutral-200)]"
                )}
                title="Thème auto"
                data-testid="theme-system-btn"
              >
                <Monitor className="h-4 w-4" style={{ color: themePref === 'system' ? 'var(--primary-400)' : 'var(--text-secondary)' }} />
              </button>
            </div>
          </div>
        </header>
      </div>
    </>
  );
};

export default Navigation;
